import { useState, useEffect } from 'react';
import { api } from '../api';
import {
  PageHeader, Card, Field, Select, Empty, SourceNote, InfoDot, Pill,
} from '../components/ui';

const H = (v) => (v ? `${Number(v).toFixed(0)}h` : '');

const SOURCES = [
  { source: 'Magnetic', label: 'Resource allocations', detail: '/scheduling/resources_report — one row per (person × task) with start/end + allocated hours' },
  { source: 'Derived', label: 'Per-bucket hours', detail: 'allocated hours spread evenly across the working days in the task span, then summed by week or month' },
  { source: 'BambooHR', label: 'Leave / holidays', detail: 'Magnetic does not subtract these; we apply the correction in the Utilisation view (and on capacity sync), so the schedule grid shows raw Magnetic plans' },
];

// shade cells by allocation intensity (rough): >40h/week = red, 20-40 amber, 1-20 green
const cellTone = (h, bucketsLen) => {
  if (!h) return '';
  const perWeekEq = bucketsLen <= 4 ? h / 1 : h;       // already weekly buckets in default mode
  if (perWeekEq > 40) return 'bg-brand-500/20 text-brand-500';
  if (perWeekEq > 20) return 'bg-amber-500/15 text-amber-600';
  return 'bg-emerald-500/10 text-emerald-600';
};

const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

export default function Schedule() {
  const [gran, setGran] = useState('week');
  const [start, setStart] = useState(plusDays(-42));
  const [end, setEnd] = useState(plusDays(56));
  const [team, setTeam] = useState('');
  const [person, setPerson] = useState('');
  const [project, setProject] = useState('');
  const [openOnly, setOpenOnly] = useState(true);

  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ teams: [], people: [], projects: [] });

  useEffect(() => { api.schedule.filters().then(setFilters).catch(() => {}); }, []);
  useEffect(() => {
    const p = { granularity: gran, start, end, open_only: openOnly };
    if (team) p.team = team;
    if (person) p.person = person;
    if (project) p.project = project;
    api.schedule.grid(p).then(setData).catch(() => {});
  }, [gran, start, end, team, person, project, openOnly]);

  const buckets = data?.buckets || [];

  return (
    <div>
      <PageHeader
        eyebrow="Reporting"
        title="Schedule"
        subtitle="Who is allocated to what, per week or month — the same data Magnetic shows on its resource grid. Allocated hours are spread evenly across the working days in each task span."
        actions={
          <div className="flex items-end gap-3">
            <Field label="View"><Select value={gran} onChange={(e) => setGran(e.target.value)}>
              <option value="week">Weekly</option><option value="month">Monthly</option>
            </Select></Field>
            <Field label="From"><input type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100" /></Field>
            <Field label="To"><input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100" /></Field>
          </div>
        }
      />

      <SourceNote items={SOURCES} />

      <Card padding="p-4" className="mb-6">
        <div className="grid grid-cols-2 xl:grid-cols-5 gap-3 items-end">
          <Field label="Team"><Select value={team} onChange={(e) => setTeam(e.target.value)}>
            <option value="">All teams</option>
            {filters.teams.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select></Field>
          <Field label="Person"><Select value={person} onChange={(e) => setPerson(e.target.value)}>
            <option value="">All people</option>
            {filters.people.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
          </Select></Field>
          <Field label="Project"><Select value={project} onChange={(e) => setProject(e.target.value)}>
            <option value="">All projects</option>
            {filters.projects.map((p) => <option key={p} value={p}>{p}</option>)}
          </Select></Field>
          <label className="flex items-center gap-2 text-sm text-slate-300 mt-6">
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)}
              className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-brand-500" />
            Open tasks only
          </label>
          <div className="text-xs text-slate-500 mt-6">
            {data && <>{data.people.length} people · {buckets.length} {gran === 'week' ? 'weeks' : 'months'}</>}
          </div>
        </div>
      </Card>

      {!data ? <Card><Empty>Loading…</Empty></Card>
       : data.people.length === 0 ? <Card><Empty>No allocations in this window. Try widening the date range or removing filters.</Empty></Card>
       : (
        <Card padding="p-0">
          <div className="overflow-x-auto">
            <table className="text-sm border-separate border-spacing-0 min-w-full">
              <thead className="sticky top-0 bg-slate-800 z-10">
                <tr className="text-[11px] font-medium uppercase tracking-[0.1em] text-slate-500">
                  <th className="px-3 py-2.5 border-b border-slate-700 text-left sticky left-0 bg-slate-800 z-20 min-w-[220px]">Person</th>
                  <th className="px-3 py-2.5 border-b border-slate-700 text-left min-w-[140px]">Team</th>
                  {buckets.map((b) => (
                    <th key={b} className="px-2 py-2.5 border-b border-slate-700 text-right min-w-[64px] font-mono">{b.replace(/^\d{4}-/, '')}</th>
                  ))}
                  <th className="px-3 py-2.5 border-b border-slate-700 text-right min-w-[72px]">
                    <span className="inline-flex items-center gap-1 justify-end">Total <InfoDot>Sum across buckets in the current window.</InfoDot></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.people.map((p) => (
                  <tr key={p.email} className="group">
                    <td className="px-3 py-2 border-b border-slate-700/60 sticky left-0 bg-slate-800 group-hover:bg-slate-700/40 z-10">
                      <div className="text-slate-200">{p.name}</div>
                      <div className="text-[11px] text-slate-500">{p.position}</div>
                    </td>
                    <td className="px-3 py-2 border-b border-slate-700/60 text-slate-400">
                      {p.department}{p.team ? <span className="text-slate-600"> · {p.team}</span> : null}
                    </td>
                    {buckets.map((b) => (
                      <td key={b} className={`px-2 py-2 border-b border-slate-700/60 text-right nums ${cellTone(p.buckets[b], buckets.length)}`}>
                        {H(p.buckets[b])}
                      </td>
                    ))}
                    <td className="px-3 py-2 border-b border-slate-700/60 text-right nums font-semibold text-slate-100">{H(p.total)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-900/40">
                  <td className="px-3 py-2 sticky left-0 bg-slate-900/60 text-[11px] uppercase tracking-[0.1em] text-slate-500">Bucket totals</td>
                  <td />
                  {buckets.map((b) => (
                    <td key={b} className="px-2 py-2 text-right nums text-slate-300">{H(data.bucket_totals[b])}</td>
                  ))}
                  <td className="px-3 py-2 text-right nums text-slate-200 font-semibold">
                    {H(Object.values(data.bucket_totals).reduce((s, v) => s + v, 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {data && data.people.length > 0 && (
        <Card title="Allocation legend" className="mt-6" subtitle="Cell colour reflects rough weekly intensity.">
          <div className="flex gap-3 text-xs">
            <Pill tone="ok">≤ 20h</Pill><Pill tone="warn">20–40h</Pill><Pill tone="danger">&gt; 40h</Pill>
            <span className="text-slate-500">— spread of each task's allocated hours across its working-day span.</span>
          </div>
        </Card>
      )}
    </div>
  );
}
